<template>
  <v-text-field
    v-model="number"
    :label="$t('Telephone')"
    :error-messages="numberError"
    v-bind="$attrs"
  >
    <template v-slot:prepend-inner>
      <v-menu
        v-if="country"
        max-height="250"
      >
        <template v-slot:activator="{on}">
          <div
            class="emoji px-1"
            v-on="on">
            <span style="font-size: 26px">{{ country.emoji }}</span>
          </div>
        </template>
        <v-list>
          <v-list-tile
            v-for="country in prefixes"
            :key="country.code"
            @click="changePrefix(country)"
          >
            <v-list-tile-title>
              {{ prefixElement(country) }}
            </v-list-tile-title>
          </v-list-tile>
        </v-list>
      </v-menu>
    </template>
  </v-text-field>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import parsePhoneNumber from 'libphonenumber-js'

export default {
  name: 'TelephoneField',
  props: {

    value: {
      default: null,
      validator: function (value) {
        if (value === null) {
          return true
        }

        if (typeof value !== 'object') {
          return false
        }

        return value.hasOwnProperty('prefix') && value.hasOwnProperty('number')
      }
    }
  },

  data () {
    return {
      numberError: '',
      country: null
    }
  },

  computed: {
    ...mapGetters(['countries', 'allowedPhonePrefixes']),
    prefix: {
      get () {
        return this.value ? this.value.prefix || '' : ''
      },
      set (value) {
        this.$emit('input', {
          prefix: value,
          number: this.number
        })
      }
    },
    number: {
      get () {
        return this.value ? this.value.number || '' : ''
      },
      set (value) {
        if (!value) {
          this.$emit('input', null)
          return
        }

        this.$emit('input', {
          prefix: this.prefix || this.country.dialling.prefix,
          number: value
        })
      }
    },
    prefixes () {
      const countries = this.countries.filter(item => item.dialling.prefix)
      if (!this.allowedPhonePrefixes || !this.allowedPhonePrefixes.length) {
        return countries
      }

      return countries.filter(item => this.allowedPhonePrefixes.includes(item.code))
    }
  },

  watch: {
    number: function () {
      this.validateNumber()
    }
  },

  async mounted () {
    if (this.countries.length === 0) {
      await this.initCountries()
    }

    if (this.allowedPhonePrefixes.length === 0) {
      await this.initAllowedLists()
    }

    this.setCountry()
  },

  methods: {
    ...mapActions(['setCountries', 'setAllowedLists']),

    async initCountries () {
      const countries = await this.$http.make('country').get()
      this.setCountries(countries)
    },

    async initAllowedLists () {
      const settings = await this.$http.make('settings').find('localisation/countries')
      this.setAllowedLists(settings)
    },

    prefixElement (country) {
      return `${country.emoji} ${country.name} +${country.dialling.prefix}`
    },

    changePrefix (country) {
      this.prefix = country.dialling.prefix
      this.country = country
      this.validateNumber()
    },

    setCountry () {
      if (!this.prefix) {
        this.country = this.prefixes[0]
        return
      }

      if (!this.number) {
        const countries = this.prefixes.filter(country => country.dialling.prefix === this.prefix)
        if (countries.length > 0) {
          this.country = countries[0]
        }
        return
      }

      const number = parsePhoneNumber(`+${this.prefix}${this.number}`)
      const countryCode = number.country
      const countries = this.prefixes.filter(country => country.code === countryCode)
      if (countries.length > 0) {
        this.country = countries[0]
      }
    },

    validateNumber () {
      if (!this.country || !this.prefix) {
        this.numberError = ''
        return
      }

      const phoneNumber = parsePhoneNumber(`+${this.prefix}${this.number}`, this.country.code)
      if (phoneNumber && !phoneNumber.isValid()) {
        this.numberError = ['The number is invalid.']
        return
      }

      this.numberError = ''
    }
  }
}
</script>

<style scoped>
  .emoji:hover {
    cursor: pointer;
  }

  .emoji span {
    font-size: 26px;
  }
</style>
